<template>
  <v-container fluid>
    <div class="d-flex align-center flex-wrap mb-4 cotizaciones__header">
      <h2 class="text-h5 mr-3">Cotizaciones</h2>
      <v-spacer />
      <v-btn color="primary" to="/cotizador">Nueva cotización</v-btn>
    </div>

    <v-row dense>
      <v-col cols="12" sm="6" md="3">
        <v-text-field v-model="filters.clientName" label="Cliente" dense outlined clearable hide-details />
      </v-col>
      <v-col cols="6" sm="3" md="2">
        <v-select v-model="filters.countryIso" :items="countries" label="País" dense outlined clearable hide-details />
      </v-col>
      <v-col cols="6" sm="3" md="2">
        <v-select v-model="filters.line" :items="lines" label="Línea" dense outlined clearable hide-details />
      </v-col>
      <v-col cols="6" sm="3" md="2">
        <v-select
          v-model="seller"
          :items="sellerItems"
          label="Canal / broker"
          dense
          outlined
          clearable
          hide-details
        />
      </v-col>
      <v-col cols="6" sm="3" md="2">
        <v-select v-model="filters.status" :items="statuses" label="Estado" dense outlined clearable hide-details />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-select
          v-model="filters.createdBy"
          :items="executiveItems"
          label="Ejecutivo"
          dense
          outlined
          clearable
          hide-details
        />
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="rows"
      :loading="loading"
      :server-items-length="total"
      :options.sync="tableOptions"
      :footer-props="{ itemsPerPageOptions: [20, 50, 100] }"
      class="mt-4 cotizaciones__table"
      @click:row="open"
    >
      <template #[`item.clientName`]="{ item }">
        {{ item.clientName }}<span v-if="item.version > 1" class="text-caption"> · v{{ item.version }}</span>
      </template>
      <template #[`item.status`]="{ item }">
        <v-chip small outlined>{{ statusLabel(item.status) }}</v-chip>
      </template>
      <template #[`item.channel`]="{ item }">{{ item.brokerName || channelLabel(item.channel) }}</template>
      <template #[`item.createdBy`]="{ item }">{{ executiveName(item.createdBy) }}</template>
      <template #[`item.proposalUrl`]="{ item }">
        <!-- .stop: opening the proposal must not also open the quote. -->
        <v-btn v-if="item.proposalUrl" text small :href="item.proposalUrl" target="_blank" rel="noopener" @click.stop>
          Ver propuesta
        </v-btn>
      </template>
    </v-data-table>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" top>{{ snackbarMessage }}</v-snackbar>
  </v-container>
</template>

<script>
import B2bQuotesService from '../services/b2b-quotes.service';
import EjecutivosService from '../services/ejecutivos.service';
import BrokerService from '../services/broker.service';
import { utilsMixin } from '../mixins/utilsMixin';
import {
  CHANNEL_LABELS,
  COUNTRY_OPTIONS,
  LINE_OPTIONS,
  STATUS_LABELS,
  errorMessage
} from '../utils/b2b-quotes.utils';

const FILTER_DEBOUNCE_MS = 350;

export default {
  name: 'Cotizaciones',
  mixins: [utilsMixin],
  data() {
    return {
      rows: [],
      total: 0,
      loading: false,
      tableOptions: { page: 1, itemsPerPage: 20 },
      filters: {
        clientName: null,
        countryIso: null,
        line: null,
        channel: null,
        brokerId: null,
        status: null,
        createdBy: null
      },
      filterTimer: null,
      executives: [],
      countries: COUNTRY_OPTIONS,
      lines: LINE_OPTIONS,
      brokers: [],
      seller: null,
      statuses: Object.entries(STATUS_LABELS).map(([value, text]) => ({ value, text })),
      headers: [
        { text: 'ID Propuesta', value: 'proposalCode', sortable: false },
        { text: 'Cliente', value: 'clientName', sortable: false },
        { text: 'País', value: 'countryIso', sortable: false },
        { text: 'Canal / broker', value: 'channel', sortable: false },
        { text: 'Estado', value: 'status', sortable: false },
        { text: 'Ejecutivo', value: 'createdBy', sortable: false },
        { text: '', value: 'proposalUrl', sortable: false, align: 'end' }
      ],
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: ''
    };
  },
  computed: {
    /** "Directo" plus each broker: a quote is sold direct or through one broker. */
    sellerItems() {
      return [
        { value: 'direct', text: 'Directo' },
        ...this.brokers.map((b) => ({ value: `broker:${b.id}`, text: b.name }))
      ];
    },
    executiveItems() {
      return this.executives.map((e) => ({ value: e.id, text: `${e.name} ${e.surname ?? ''}`.trim() }));
    }
  },
  watch: {
    seller(value) {
      this.filters.channel = value === 'direct' ? 'direct' : null;
      this.filters.brokerId = value && value.startsWith('broker:') ? value.slice('broker:'.length) : null;
    },
    tableOptions: {
      deep: true,
      handler() {
        this.load();
      }
    },
    filters: {
      deep: true,
      handler() {
        clearTimeout(this.filterTimer);
        this.filterTimer = setTimeout(() => {
          this.tableOptions = { ...this.tableOptions, page: 1 };
          this.load();
        }, FILTER_DEBOUNCE_MS);
      }
    }
  },
  async created() {
    const [executives] = await Promise.all([EjecutivosService.getAll(true), this.loadBrokers()]);
    if (executives.status === 'ok') this.executives = executives.data;
  },
  beforeDestroy() {
    clearTimeout(this.filterTimer);
  },
  methods: {
    async loadBrokers() {
      const res = await BrokerService.getAll();
      if (res.status === 'ok') this.brokers = res.data.map((b) => ({ id: b.id, name: b.name }));
    },
    async load() {
      this.loading = true;
      const params = { page: this.tableOptions.page, pageSize: this.tableOptions.itemsPerPage };
      Object.entries(this.filters).forEach(([key, value]) => {
        if (value) params[key] = value;
      });
      const res = await B2bQuotesService.list(params);
      if (res.status === 'ok') {
        this.rows = res.data;
        this.total = res.meta.total;
      } else {
        this.showErrorSnackbar(errorMessage(res.code));
      }
      this.loading = false;
    },
    open(row) {
      this.$router.push(`/cotizaciones/${row.id}`);
    },
    executiveName(id) {
      const executive = this.executives.find((e) => e.id === id);
      return executive ? `${executive.name} ${executive.surname ?? ''}`.trim() : '—';
    },
    statusLabel(status) {
      return STATUS_LABELS[status] ?? status;
    },
    channelLabel(channel) {
      return CHANNEL_LABELS[channel] ?? channel;
    }
  }
};
</script>

<style scoped>
.cotizaciones__header {
  gap: 8px;
}
.cotizaciones__table ::v-deep tbody tr {
  cursor: pointer;
}
</style>
